import { useEffect, useRef, useState } from "react";
import { io } from "socket.io-client";
import { api, API } from "./api";
import Remote from "./Remote";
import {
  Monitor,
  MessageCircle,
  Users,
  Shield,
  Copy,
  RefreshCw,
  Send,
  Power,
  FolderUp,
  CreditCard,
} from "lucide-react";
const defaultPerm = { screen: true, mouse: true, keyboard: true, files: true };
function Login({ done }) {
  const [register, setRegister] = useState(false),
    [form, setForm] = useState({ name: "", email: "", password: "" }),
    [error, setError] = useState("");
  async function go(e) {
    e.preventDefault();
    try {
      const d = await api(`/api/auth/${register ? "register" : "login"}`, {
        method: "POST",
        body: JSON.stringify(form),
      });
      localStorage.token = d.token;
      done(d.user);
    } catch (x) {
      setError(x.message);
    }
  }
  return (
    <main className="login">
      <div className="login-card">
        <div className="brand">
          O<span>◉</span> OjMee Orbit
        </div>
        <h1>{register ? "Create your Orbit" : "Welcome back"}</h1>
        <p>Secure remote access for your team and devices.</p>
        <form onSubmit={go}>
          {register && (
            <input
              placeholder="Full name"
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          )}
          <input
            type="email"
            placeholder="Email"
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
          <input
            type="password"
            placeholder="Password (8+ characters)"
            onChange={(e) => setForm({ ...form, password: e.target.value })}
          />
          {error && <div className="error">{error}</div>}
          <button>{register ? "Create account" : "Sign in"}</button>
        </form>
        <a onClick={() => setRegister(!register)}>
          {register
            ? "Already registered? Sign in"
            : "New here? Create account"}
        </a>
      </div>
    </main>
  );
}
function LegacyRemote({ user, socket, sessionMessage }) {
  const [code, setCode] = useState("------"),
    [expires, setExpires] = useState(null),
    [target, setTarget] = useState(""),
    [password, setPassword] = useState(""),
    [status, setStatus] = useState("Ready"),
    video = useRef();
  async function rotate() {
    try {
      const d = await api("/api/auth/rotate-access-password", {
        method: "POST",
      });
      setCode(d.accessPassword);
      setExpires(new Date(d.expiresAt));
    } catch (error) {
      setStatus(error.message || "Unable to generate access password");
    }
  }
  useEffect(() => {
    rotate();
    const t = setInterval(rotate, 120000);
    return () => clearInterval(t);
  }, []);
  async function connect() {
    try {
      if (!target.trim() || !password.trim()) {
        setStatus("Enter the target Orbit ID and temporary password.");
        return;
      }
      if (!socket?.connected) {
        setStatus("Real-time server is offline. Please wait and try again.");
        return;
      }
      setStatus("Checking Orbit ID and temporary password…");
      const d = await api("/api/auth/verify-access", {
        method: "POST",
        body: JSON.stringify({
          orbitId: target.trim(),
          accessPassword: password.trim(),
        }),
      });
      setStatus("Sending request to target device…");
      socket.timeout(10000).emit(
        "session:request",
        {
          targetId: String(d.target.id),
          accessGrant: d.accessGrant,
          permissions: defaultPerm,
          deviceId: localStorage.getItem("deviceId"),
        },
        (timeoutError, response) => {
          if (timeoutError) {
            setStatus("Server did not respond. Please try again.");
            return;
          }
          if (!response?.ok) {
            setStatus(response?.message || "Unable to send request.");
            return;
          }
          setStatus("Request delivered. Waiting for target user approval…");
        },
      );
    } catch (e) {
      setStatus(e.message || "Unable to connect");
    }
  }

  if (
    accept &&
    incomingSession.permissions?.screen &&
    !navigator.mediaDevices?.getDisplayMedia
  ) {
    setSessionMessage(
      "Screen sharing is not supported in this mobile browser. Open OjMee Orbit on a desktop or use the Android app.",
    );

    return;
  }

  async function share() {
    const stream = await navigator.mediaDevices.getDisplayMedia({
      video: true,
      audio: true,
    });
    video.current.srcObject = stream;
    setStatus("Sharing screen (local preview)");
  }
  return (
    <>
      <div className="hero">
        <div>
          <small>THIS DEVICE</small>
          <h2>{user.orbitId}</h2>
          <p>
            Share this ID and temporary password only with people you trust.
          </p>
        </div>
        <div className="pass">
          <small>2-MINUTE PASSWORD</small>
          <strong>{code}</strong>
          <button className="icon" onClick={rotate}>
            <RefreshCw size={18} />
          </button>
          <span>{expires && `Expires ${expires.toLocaleTimeString()}`}</span>
        </div>
      </div>
      <div className="grid2">
        <section className="panel">
          <h3>Connect to another device</h3>
          <input
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            placeholder="Orbit ID: 123 456 789"
          />
          <input
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Temporary password"
          />
          <div className="checks">Screen • Mouse • Keyboard • Files</div>
          <button onClick={connect}>Request secure session</button>
          <p className="muted">{sessionMessage || status}</p>
        </section>
        <section className="panel">
          <h3>Share this screen</h3>
          <video ref={video} autoPlay muted />
          <button className="secondary" onClick={share}>
            <Monitor size={18} /> Start screen share preview
          </button>
        </section>
      </div>
    </>
  );
}
function Chat({ socket }) {
  const [to, setTo] = useState(""),
    [text, setText] = useState(""),
    [items, setItems] = useState([]);
  useEffect(() => {
    const f = (m) => setItems((x) => [...x, m]);
    socket.on("chat:message", f);
    return () => socket.off("chat:message", f);
  }, []);
  function send() {
    if (text && to) {
      socket.emit("chat:send", { to, text });
      setText("");
    }
  }
  return (
    <section className="panel chat">
      <h3>Real-time chat</h3>
      <input
        placeholder="Recipient database user ID"
        value={to}
        onChange={(e) => setTo(e.target.value)}
      />
      <div className="messages">
        {items.map((m, i) => (
          <div key={i}>{m.text}</div>
        ))}
      </div>
      <div className="compose">
        <input
          placeholder="Write a message"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
        />
        <button className="icon" onClick={send}>
          <Send />
        </button>
      </div>
    </section>
  );
}
function Admin() {
  const [stats, setStats] = useState({}),
    [users, setUsers] = useState([]);
  useEffect(() => {
    api("/api/admin/stats")
      .then(setStats)
      .catch(() => {});
    api("/api/admin/users")
      .then((d) => setUsers(d.users))
      .catch(() => {});
  }, []);
  return (
    <>
      <div className="stats">
        {Object.entries(stats).map(([k, v]) => (
          <div>
            <small>{k}</small>
            <b>{v}</b>
          </div>
        ))}
      </div>
      <section className="panel">
        <h3>Users & devices</h3>
        <div className="table">
          {users.map((u) => (
            <div className="row">
              <span>
                <b>{u.name}</b>
                <small>{u.email}</small>
              </span>
              <span>{u.orbitId}</span>
              <span className={u.isActive ? "ok" : "off"}>
                {u.isActive ? "Active" : "Disabled"}
              </span>
              <span>{u.role}</span>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}
const planCopy = {
  free: ["Free", "₹0", "99 remote minutes", "No file transfer"],
  essential_monthly: [
    "Essential",
    "₹99/user/month",
    "Unlimited remote access",
    "No file transfer",
  ],
  full_monthly: [
    "Full Access",
    "₹149/user/month",
    "Unlimited remote access",
    "File transfer included",
  ],
  full_yearly: [
    "Full Access Yearly",
    "₹1,499/year",
    "Unlimited remote access",
    "File transfer included",
  ],
};
function loadScript(src) {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) return resolve();
    const script = document.createElement("script");
    script.src = src;
    script.onload = resolve;
    script.onerror = reject;
    document.head.appendChild(script);
  });
}
function Billing({ user }) {
  const [subscription, setSubscription] = useState(null),
    [seats, setSeats] = useState(1),
    [gateway, setGateway] = useState("cashfree"),
    [busy, setBusy] = useState("");
  useEffect(() => {
    api("/api/billing/subscription").then((d) =>
      setSubscription(d.subscription),
    );
  }, []);
  async function buy(planId) {
    try {
      setBusy(planId);
      const d = await api("/api/billing/checkout", {
        method: "POST",
        body: JSON.stringify({ planId, gateway, seats, email: user.email }),
      });
      if (gateway === "cashfree") {
        await loadScript("https://sdk.cashfree.com/js/v3/cashfree.js");
        await window.Cashfree({ mode: d.mode }).checkout({
          paymentSessionId: d.paymentSessionId,
          redirectTarget: "_self",
        });
      } else {
        const base =
          import.meta.env.VITE_PAYTM_HOST || "https://securegw-stage.paytm.in";
        await loadScript(
          `${base}/merchantpgpui/checkoutjs/merchants/${d.mid}.js`,
        );
        await window.Paytm.CheckoutJS.init({
          root: "",
          flow: "DEFAULT",
          data: {
            orderId: d.orderId,
            token: d.txnToken,
            tokenType: "TXN_TOKEN",
            amount: String(d.amount),
          },
          handler: { notifyMerchant: console.log },
        });
        window.Paytm.CheckoutJS.invoke();
      }
    } catch (e) {
      alert(e.message);
      setBusy("");
    }
  }
  const current = subscription?.planId || "free";
  return (
    <>
      <section className="usage-card">
        <div>
          <small>CURRENT PLAN</small>
          <h2>{planCopy[current][0]}</h2>
          <p>
            {subscription?.fileTransfer
              ? "File transfer enabled"
              : "File transfer unavailable"}
          </p>
        </div>
        <div className="minutes">
          <small>REMOTE MINUTES</small>
          <strong>
            {subscription?.unlimited
              ? "Unlimited"
              : `${subscription?.remainingMinutes ?? 99} remaining`}
          </strong>
          {!subscription?.unlimited && (
            <span>
              {subscription?.usedMinutes || 0} used of{" "}
              {subscription?.totalMinutes || 99}
            </span>
          )}
        </div>
      </section>
      <div className="billing-options">
        <label>
          Payment gateway
          <select value={gateway} onChange={(e) => setGateway(e.target.value)}>
            <option value="cashfree">Cashfree</option>
            <option value="paytm">Paytm</option>
          </select>
        </label>
        <label>
          Users/seats
          <input
            type="number"
            min="1"
            max="100"
            value={seats}
            onChange={(e) => setSeats(Math.max(1, Number(e.target.value)))}
          />
        </label>
      </div>
      <div className="plans">
        {Object.entries(planCopy).map(([id, p]) => (
          <section
            className={`plan ${id === "full_monthly" ? "featured" : ""}`}
            key={id}
          >
            {id === "full_monthly" && <em>POPULAR</em>}
            <h3>{p[0]}</h3>
            <div className="price">{p[1]}</div>
            <p>✓ {p[2]}</p>
            <p>
              {p[3].includes("No") ? "—" : "✓"} {p[3]}
            </p>
            {id === "free" ? (
              <button disabled>Included automatically</button>
            ) : (
              <button
                disabled={busy === id || current === id}
                onClick={() => buy(id)}
              >
                {current === id
                  ? "Current plan"
                  : busy === id
                    ? "Opening checkout…"
                    : "Choose plan"}
              </button>
            )}
          </section>
        ))}
      </div>
    </>
  );
}

function ConsentModal({ session, busy, onRespond }) {
  if (!session) return null;

  const permissionRows = [
    ["Screen sharing", session.permissions?.screen],
    ["Mouse control", session.permissions?.mouse],
    ["Keyboard control", session.permissions?.keyboard],
    ["File transfer", session.permissions?.files],
  ];

  return (
    <div style={consentStyles.overlay} role="dialog" aria-modal="true">
      <section style={consentStyles.modal}>
        <div style={consentStyles.icon}>
          <Monitor size={30} />
        </div>
        <small style={consentStyles.eyebrow}>SECURE CONNECTION REQUEST</small>
        <h2 style={consentStyles.title}>Remote access request</h2>
        <p style={consentStyles.text}>
          Another OjMee Orbit user wants to connect to this device. Review the
          requested permissions before continuing.
        </p>
        <div style={consentStyles.permissions}>
          {permissionRows.map(([label, enabled]) => (
            <div style={consentStyles.permissionRow} key={label}>
              <span>{label}</span>
              <strong style={{ color: enabled ? "#087f6f" : "#8492a6" }}>
                {enabled ? "Requested" : "Not requested"}
              </strong>
            </div>
          ))}
        </div>
        <p style={consentStyles.warning}>
          Accept only if you recognize and trust the requesting user.
        </p>
        <div style={consentStyles.actions}>
          <button
            type="button"
            disabled={busy}
            onClick={() => onRespond(false)}
            style={consentStyles.reject}
          >
            Reject
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => onRespond(true)}
            style={consentStyles.accept}
          >
            {busy ? "Please wait…" : "Accept connection"}
          </button>
        </div>
      </section>
    </div>
  );
}

const consentStyles = {
  overlay: {
    position: "fixed",
    inset: 0,
    zIndex: 9999,
    display: "grid",
    placeItems: "center",
    padding: 20,
    background: "rgba(3, 14, 29, 0.78)",
    backdropFilter: "blur(8px)",
  },
  modal: {
    width: "min(480px, 100%)",
    padding: 30,
    border: "1px solid #dce6f1",
    borderRadius: 22,
    background: "#ffffff",
    boxShadow: "0 30px 100px rgba(0, 0, 0, 0.4)",
  },
  icon: {
    width: 58,
    height: 58,
    display: "grid",
    placeItems: "center",
    borderRadius: 16,
    background: "#eaf3ff",
    color: "#0b5ed7",
    marginBottom: 18,
  },
  eyebrow: { color: "#0b5ed7", fontWeight: 800, letterSpacing: 1.2 },
  title: { margin: "8px 0", color: "#10243e" },
  text: { color: "#66778d", lineHeight: 1.6 },
  permissions: {
    display: "grid",
    gap: 10,
    margin: "22px 0",
    padding: 16,
    borderRadius: 14,
    background: "#f3f7fb",
  },
  permissionRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: 15,
    color: "#52647a",
    fontSize: 14,
  },
  warning: { color: "#b54708", fontSize: 13, lineHeight: 1.5 },
  actions: {
    display: "grid",
    gridTemplateColumns: "1fr 1.5fr",
    gap: 12,
    marginTop: 22,
  },
  reject: { background: "#edf1f6", color: "#394b61" },
  accept: {
    background: "linear-gradient(135deg, #0b5ed7, #087cc9)",
    color: "#ffffff",
  },
};

export default function App() {
  const [user, setUser] = useState(null);
  const [tab, setTab] = useState("remote");
  const [socket, setSocket] = useState(null);
  const [socketConnected, setSocketConnected] = useState(false);
  const [incomingSession, setIncomingSession] = useState(null);
  const [sessionMessage, setSessionMessage] = useState("");
  const [responding, setResponding] = useState(false);
  const [acceptedScreenStream, setAcceptedScreenStream] = useState(null);
  const [activeSession, setActiveSessionState] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("activeOrbitSession") || "null");
    } catch {
      return null;
    }
  });
  function setActiveSession(session) {
    setActiveSessionState(session);
    if (session) localStorage.setItem("activeOrbitSession", JSON.stringify(session));
    else localStorage.removeItem("activeOrbitSession");
  }

  function clearRemoteSession(message = "Remote session ended.") {
    setAcceptedScreenStream((currentStream) => {
      currentStream?.getTracks().forEach((track) => track.stop());
      return null;
    });
    setIncomingSession(null);
    setActiveSession(null);
    setSessionMessage(message);
  }
  useEffect(() => {
    if (localStorage.token)
      api("/api/auth/me")
        .then((d) => setUser(d.user))
        .catch(() => localStorage.clear());
  }, []);
  useEffect(() => {
    if (user) {
      if (!localStorage.getItem("deviceId")) {
        localStorage.setItem("deviceId", crypto.randomUUID());
      }
      const s = io(API, {
        auth: {
          token: localStorage.getItem("token"),
          deviceId: localStorage.getItem("deviceId"),
        },
        reconnection: true,
        reconnectionAttempts: Infinity,
        reconnectionDelay: 1000,
      });
      setSocket(s);
      return () => {
        s.removeAllListeners();
        s.close();
        setSocketConnected(false);
      };
    }
  }, [user]);

  useEffect(() => {
    if (!socket) return;

    const handleConnect = () => {
      setSocketConnected(true);
      setSessionMessage("");
      console.log("OjMee Orbit socket connected:", socket.id);
      if (activeSession?.sessionId) socket.emit("session:join", { sessionId: activeSession.sessionId });
    };
    const handleDisconnect = (reason) => {
      setSocketConnected(false);
      setSessionMessage(`Real-time server disconnected: ${reason}`);
    };
    const handleConnectError = (error) => {
      setSocketConnected(false);
      setSessionMessage(`Real-time connection error: ${error.message}`);
      console.error("Socket connection error:", error);
    };
    const handleIncomingRequest = (sessionData) => {
      console.log("Incoming remote session request:", sessionData);
      setIncomingSession(sessionData);
      setSessionMessage("A remote access request needs your approval.");
    };
    const handleSessionResponse = (response) => {
      console.log("Remote session response:", response);
      if (response.accepted || response.status === "accepted") {
        setActiveSession(response);
        socket.emit("session:join", { sessionId: response.sessionId });
        setSessionMessage(
          "Remote request accepted. Establishing secure connection…",
        );
      } else {
        setSessionMessage("Remote connection request was rejected.");
      }
    };
    const handleSessionError = (error) => {
      setSessionMessage(error?.message || "Remote session error");
    };
    const handleSessionEnded = (payload) => {
      if (
        payload?.sessionId &&
        activeSession?.sessionId &&
        payload.sessionId !== activeSession.sessionId
      ) {
        return;
      }
      clearRemoteSession(payload?.message || "Remote session ended by partner.");
    };

    socket.on("connect", handleConnect);
    socket.on("disconnect", handleDisconnect);
    socket.on("connect_error", handleConnectError);
    socket.on("session:requested", handleIncomingRequest);
    socket.on("session:response", handleSessionResponse);
    socket.on("session:error", handleSessionError);
    socket.on("session:ended", handleSessionEnded);

    if (socket.connected) handleConnect();

    return () => {
      socket.off("connect", handleConnect);
      socket.off("disconnect", handleDisconnect);
      socket.off("connect_error", handleConnectError);
      socket.off("session:requested", handleIncomingRequest);
      socket.off("session:response", handleSessionResponse);
      socket.off("session:error", handleSessionError);
      socket.off("session:ended", handleSessionEnded);
    };
  }, [socket, activeSession?.sessionId]);

  function disconnectRemoteSession() {
    const sessionId = activeSession?.sessionId;
    if (!sessionId) return;

    // Host और viewer दोनों यही event भेज सकते हैं। Server दोनों participants
    // को session:ended भेजकर session को बंद करेगा।
    if (socket?.connected) {
      socket.timeout(5000).emit(
        "session:end",
        { sessionId },
        (timeoutError, response) => {
          if (timeoutError) {
            console.warn("Session end acknowledgement timed out.");
          } else if (response && !response.ok) {
            console.warn(response.message || "Server could not end session.");
          }
        },
      );
    }

    clearRemoteSession("You disconnected the remote session.");
  }

  async function respondToSession(accept) {
    if (!incomingSession || !socket?.connected || responding) return;

    let selectedStream = null;
    if (accept && incomingSession.permissions?.screen) {
      try {
        selectedStream = await navigator.mediaDevices.getDisplayMedia({
          video: {
            width: { ideal: 1280, max: 1600 },
            height: { ideal: 720, max: 900 },
            frameRate: { ideal: 20, max: 24 },
          },
          audio: false,
        });

        const videoTrack = selectedStream.getVideoTracks()[0];
        if (videoTrack) {
          videoTrack.contentHint = "motion";
        }
      } catch (error) {
        setSessionMessage(
          error.name === "NotAllowedError"
            ? "Screen permission is required to accept this request."
            : `Unable to capture screen: ${error.message}`,
        );
        return;
      }
    }

    setResponding(true);
    socket
      .timeout(10000)
      .emit(
        "session:respond",
        { sessionId: incomingSession.sessionId, accept },
        (timeoutError, response) => {
          setResponding(false);

          if (timeoutError) {
            selectedStream?.getTracks().forEach((track) => track.stop());
            setSessionMessage("Unable to respond. The server timed out.");
            return;
          }
          if (!response?.ok) {
            selectedStream?.getTracks().forEach((track) => track.stop());
            setSessionMessage(response?.message || "Unable to respond.");
            return;
          }

          if (accept) {
            setActiveSession(response);
            setAcceptedScreenStream(selectedStream);
            socket.emit("session:join", {
              sessionId: incomingSession.sessionId,
            });
            setSessionMessage("Remote session accepted.");
          } else {
            setSessionMessage("Remote session rejected.");
          }

          setIncomingSession(null);
        },
      );
  }
  if (!user) return <Login done={setUser} />;
  if (!socket) return <div className="loading">Connecting Orbit…</div>;
  return (
    <div className="app">
      <aside>
        <div className="brand">
          O<span>◉</span> Orbit
        </div>
        <nav>
          <button
            className={tab === "remote" ? "active" : ""}
            onClick={() => setTab("remote")}
          >
            <Monitor />
            Remote
          </button>
          <button
            className={tab === "chat" ? "active" : ""}
            onClick={() => setTab("chat")}
          >
            <MessageCircle />
            Chat
          </button>
          <button
            className={tab === "billing" ? "active" : ""}
            onClick={() => setTab("billing")}
          >
            <CreditCard />
            Subscription
          </button>
          {user.role !== "user" && (
            <button
              className={tab === "admin" ? "active" : ""}
              onClick={() => setTab("admin")}
            >
              <Shield />
              Admin
            </button>
          )}
        </nav>
        <button
          className="logout"
          onClick={() => {
            localStorage.clear();
            location.reload();
          }}
        >
          <Power />
          Sign out
        </button>
      </aside>
      <main>
        <header>
          <div>
            <h1>{tab[0].toUpperCase() + tab.slice(1)}</h1>
            <p>Securely connected as {user.name}</p>
          </div>
          <span className={socketConnected ? "online" : "off"}>
            ● {socketConnected ? "Online" : "Reconnecting"}
          </span>
        </header>
        {sessionMessage && (
          <div
            style={{
              marginBottom: 18,
              padding: "12px 16px",
              border: "1px solid #b9dafc",
              borderRadius: 12,
              background: "#eaf4ff",
              color: "#155ca5",
              fontWeight: 600,
            }}
          >
            {sessionMessage}
          </div>
        )}
        {/*
         * Remote को navigation के समय unmount नहीं करना है। Unmount होने पर
         * WebRTC cleanup screen track और peer connection बंद कर देता है।
         */}
        <section style={{ display: tab === "remote" ? "block" : "none" }}>
          <Remote
            user={user}
            socket={socket}
            sessionMessage={sessionMessage}
            activeSession={activeSession}
            acceptedScreenStream={acceptedScreenStream}
            onDisconnectSession={disconnectRemoteSession}
            isVisible={tab === "remote"}
          />
        </section>

        {tab === "chat" && <Chat socket={socket} />}
        {tab === "billing" && <Billing user={user} />}
        {tab === "admin" && <Admin />}
      </main>
      <ConsentModal
        session={incomingSession}
        busy={responding}
        onRespond={respondToSession}
      />
    </div>
  );
}
